import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {Dropdown} from "primereact/dropdown";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import InfoExtractionDialogFields from "../../field/export/InfoExtractionDialogFields";
import MimeType from "../../../document/mimetype/MimeType";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	ExtractionFileFormat,
	ExtractionInfo,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxExtractionExtraction} from "../../../../shared/models/PortalToolboxExtractionExtraction";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class InfoExtractionDialogDefinition extends PortalDialog<PortalToolboxExtractionExtraction,
	PortalDialogProperties<PortalToolboxExtractionExtraction>,
	PortalDialogState<PortalToolboxExtractionExtraction>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<InfoExtractionDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.extractContent, Functions.getExtractContentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.EXTRACT_INFO, signatureEditProfile, Intention.reading, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkPortfolio: false, checkArchive: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["export-info-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide} resizable={false}
					header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.extract_info.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxExtractionExtraction | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(InfoExtractionDialogFields.FILE_FORMAT)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-file-format">
							<Translate {...this.props} data="extract_info.format.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<Dropdown inputId="control-file-format"
									  value={FunctionHelper.applyValue(
										  parameter?.info?.fileFormat, ExtractionInfo.getFileFormatDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "extract_info.format.xml", {ns: "dialog"}
											  ),
											  value: ExtractionFileFormat.Xml
										  },
										  {
											  label: Translate.value(
												  "extract_info.format.json", {ns: "dialog"}
											  ),
											  value: ExtractionFileFormat.Json
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  InfoExtractionDialogFields.FILE_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: InfoExtractionDialogFields, value: any): void {
		let parameter: PortalToolboxExtractionExtraction = this.state.parameter!.data;

		switch (field) {
			case InfoExtractionDialogFields.FILE_FORMAT:
				parameter.info!.fileFormat = value
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-export-info small"}/>
					<Translate {...this.props} data="extract_info.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
		);
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			extraction: this.state.parameter!.data.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.extract.info", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.EXTRACT_INFO;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxExtractionExtraction> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const InfoExtractionDialog = React.memo(InfoExtractionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxExtractionExtraction>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxExtractionExtraction>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default InfoExtractionDialog;